import React, { useMemo } from "react";
import { Card, Typography } from "antd";
import {
  DollarCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import AnimatedNumber from "animated-number-react";
import "./OrderStatsWidgets.scss";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import OrderService from "services/order";

const { Title, Text } = Typography;

const colorArr = [
  { className: "order-stats-widget-icon--primary", icon: "text-medium" },
  { className: "order-stats-widget-icon--success", icon: "text-primary" },
  { className: "order-stats-widget-icon--info", icon: "text-warning" },
  { className: "order-stats-widget-icon--warning", icon: "text-success" },
];

const formatter = (value) => value.toLocaleString();

const OrderStatsWidgets = () => {
  const { t: translation } = useTranslation();
  const { data } = useQuery("order-stats", () =>
    OrderService.getOrderStatistics()
  );
  
  const completedOrders = useMemo(
    () => Number(data?.closedOrders) || 0,
    [data]
  );
  const unprocessedOrders = useMemo(
    () =>
      Number(data?.newOrders) +
        Number(data?.confirmedOrders) +
        Number(data?.canceledOrders) +
        Number(data?.processingOrders) || 0,
    [data]
  );
  const unpaidOrders = useMemo(
    () =>
      Number(data?.unpaidOrders) +
        Number(data?.paymentProcessingOrders) +
        Number(data?.paymentFailedOrders) +
        Number(data?.paymentCanceledOrders) || 0,
    [data]
  );
  const paidOrders = useMemo(() => Number(data?.paidOrders) || 0, [data]);

  const orderStats = useMemo(
    () => ({
      completedOrders,
      unprocessedOrders,
      unpaidOrders,
      paidOrders,
    }),
    [completedOrders, unprocessedOrders, unpaidOrders, paidOrders]
  );

  const items = [
    {
      key: "unprocessedOrders",
      title: translation("orderStatsWidgets.unprocessedOrders"),
      i: <SyncOutlined />,
      ...colorArr[0], // Info color for unprocessed
    },
    {
      key: "completedOrders",
      title: translation("orderStatsWidgets.completedOrders"),
      i: <CheckCircleOutlined />,
      ...colorArr[1], // Success color for completed
    },
    {
      key: "unpaidOrders",
      title: translation("orderStatsWidgets.unpaidOrders"),
      i: <CreditCardOutlined />,
      ...colorArr[2], // Warning color for unpaid (needs attention)
    },
    {
      key: "paidOrders",
      title: translation("orderStatsWidgets.paidOrders"),
      i: <DollarCircleOutlined />,
      ...colorArr[3], // Primary color for paid
    },
  ];

  return (
    <div className="order-stats-widget-container">
      {items.map((item) => (
        <Card
          bodyStyle={{ width: "100%", padding: 0 }}
          className="order-stats-widget-card"
          key={item.title}
        >
          <div className="order-stats-widget-content">
            <div>
              <Title level={5} className="order-stats-widget-title">
                {item.title}
              </Title>
              <Title
                className={`${item.icon} order-stats-widget-value`}
                level={3}
              >
                <AnimatedNumber
                  value={orderStats[item.key]}
                  formatValue={formatter}
                  duration={600}
                />
                {item.suffix && (
                  <Text className="order-stats-widget-suffix">
                    {item.suffix}
                  </Text>
                )}
              </Title>
            </div>
            <div
              className={`order-stats-widget-icon ${item.className} ${item.icon}`}
            >
              {item.i}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default OrderStatsWidgets;
